import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { PerformanceProvider } from "@/contexts/PerformanceContext";
import Home from "./pages/Home";
import Games from "./pages/Games";
import Numbers from "./pages/Numbers";
import Lists from "./pages/Lists";
import Drawings from "./pages/Drawings";
import WebTools from "./pages/WebTools";
import Statistics from "./pages/Statistics";
import Testimonials from "./pages/Testimonials";
import LearnMore from "./pages/LearnMore";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import Sitemap from "./pages/Sitemap";
import NumbersGeneratorSitemap from "./pages/NumbersGeneratorSitemap";
import FAQ from "./pages/FAQ";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import PrivacySettings from "./pages/PrivacySettings";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import AdminPortal from "./pages/AdminPortal";
import EducationPrimary from "./pages/EducationPrimary";
import EducationHighSchool from "./pages/EducationHighSchool";
import EducationUniversity from "./pages/EducationUniversity";
import EducationCollege from "./pages/EducationCollege";
import EducationHomeschool from "./pages/EducationHomeschool";
import EducationAdult from "./pages/EducationAdult";
import Blog from "./pages/Blog";
import WhatIsRNG from "./pages/blog/WhatIsRNG";
import ProbabilityVsStatistics from "./pages/blog/ProbabilityVsStatistics";
import MonteCarloSimulation from "./pages/blog/MonteCarloSimulation";
import Alternatives from "./pages/Alternatives";
import HowItWorks from "./pages/HowItWorks";
import RandomnessVerification from "./pages/RandomnessVerification";

import SharedDraw from "./pages/SharedDraw";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <PerformanceProvider>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/games" element={<Games />} />
                <Route path="/numbers" element={<Numbers />} />
                <Route path="/lists" element={<Lists />} />
                <Route path="/drawings" element={<Drawings />} />
                <Route path="/web-tools" element={<WebTools />} />
                <Route path="/statistics" element={<Statistics />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/learn-more" element={<LearnMore />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/numbers-generator/sitemap" element={<NumbersGeneratorSitemap />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/about" element={<About />} />
                <Route path="/alternatives" element={<Alternatives />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/randomness-verification" element={<RandomnessVerification />} />
                <Route path="/draw/:shareCode" element={<SharedDraw />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/privacy-settings" element={<PrivacySettings />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin" element={<AdminPortal />} />
                <Route path="/education/primary-school" element={<EducationPrimary />} />
                <Route path="/education/high-school" element={<EducationHighSchool />} />
                <Route path="/education/university" element={<EducationUniversity />} />
                <Route path="/education/college" element={<EducationCollege />} />
                <Route path="/education/homeschool" element={<EducationHomeschool />} />
                <Route path="/education/adult-learning" element={<EducationAdult />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/what-is-random-number-generator" element={<WhatIsRNG />} />
                <Route path="/blog/probability-vs-statistics" element={<ProbabilityVsStatistics />} />
                <Route path="/blog/monte-carlo-simulation-guide" element={<MonteCarloSimulation />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PerformanceProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
